import { TellescopeModelFromName, useTellescopeInfiniteQuery } from "@tellescope/tanstack-query";

import { useTranslation } from "react-i18next";
import { MedicationCard } from "./MedicationCard";
import { useMedicationDisplay } from "./useMedicationDisplay";
import { BlockPaginatedList } from "../BlockPaginatedList";
import { BlockEmptyState } from "../BlockEmptyState";

export interface MedicationsRecordsListProps {
  contact: TellescopeModelFromName<"endusers">;
  /**
   * Renders each row's order-status and date footer. Off by default so the collapsed in-page block
   * shows the compact two-line row; the expanded/sidebar views turn it on.
   */
  showDetails?: boolean;
  /** Drills into a single medication. Rows are non-interactive when omitted. */
  onSelectMedication?: (medication: TellescopeModelFromName<"enduser_medications">) => void;
}

export const MedicationsRecordsList = ({
  contact,
  showDetails = false,
  onSelectMedication,
}: MedicationsRecordsListProps) => {
  const { t } = useTranslation("contacts");
  const getDisplayFields = useMedicationDisplay();

  const medicationsQuery = useTellescopeInfiniteQuery({
    model: "enduser_medications",
    method: "getSome",
    request: {
      filter: {
        enduserId: contact.id,
      },
    },
    select: (data) => data.pages.flat(),
  });

  if (medicationsQuery.isLoading) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <BlockEmptyState>{t("Loading medications...")}</BlockEmptyState>
      </div>
    );
  }

  if (!medicationsQuery.data?.length) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <BlockEmptyState>{t("No medications found")}</BlockEmptyState>
      </div>
    );
  }

  return (
    <BlockPaginatedList
      className="flex flex-col gap-2"
      scrollbar="floating"
      fadeWhenClipped
      onBottomReached={() => medicationsQuery.fetchNextPage()}
    >
      {medicationsQuery.data.map((medication) => {
        const { dosage, statusLabel, date } = getDisplayFields(medication);

        return (
          <MedicationCard
            key={medication.id}
            title={medication.title}
            dosage={dosage}
            statusLabel={showDetails ? statusLabel : undefined}
            date={showDetails ? date : undefined}
            onClick={onSelectMedication ? () => onSelectMedication(medication) : undefined}
          />
        );
      })}
    </BlockPaginatedList>
  );
};
